import type { UpdateConsentRequest } from '@/types/auth';

const SIGNUP_CONSENT_STORAGE_KEY = 'caloriescience_signup_consent';
const CONSENT_POLICY_VERSION = import.meta.env.VITE_CONSENT_POLICY_VERSION?.trim() || undefined;

export type SignupConsentSnapshot = {
  email: string | null;
  termsAccepted: true;
  privacyAccepted: true;
  policyVersion?: string;
};

const normalizeConsentEmail = (value: string | null | undefined): string => {
  return value?.trim().toLowerCase() ?? '';
};

export const buildSignupConsentSnapshot = (email?: string | null): SignupConsentSnapshot => ({
  email: normalizeConsentEmail(email) || null,
  termsAccepted: true,
  privacyAccepted: true,
  ...(CONSENT_POLICY_VERSION ? { policyVersion: CONSENT_POLICY_VERSION } : {}),
});

export const persistSignupConsent = (email?: string | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(
    SIGNUP_CONSENT_STORAGE_KEY,
    JSON.stringify(buildSignupConsentSnapshot(email))
  );
};

export const readStoredSignupConsent = (email?: string | null): SignupConsentSnapshot | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedConsent = window.localStorage.getItem(SIGNUP_CONSENT_STORAGE_KEY);
    if (!storedConsent) {
      return null;
    }

    const parsed = JSON.parse(storedConsent) as Partial<SignupConsentSnapshot>;
    const expectedEmail = normalizeConsentEmail(email);
    const storedEmail = normalizeConsentEmail(parsed.email);
    if (
      expectedEmail &&
      storedEmail &&
      storedEmail !== expectedEmail
    ) {
      return null;
    }

    if (parsed.termsAccepted !== true || parsed.privacyAccepted !== true) {
      return null;
    }

    return {
      email: storedEmail || expectedEmail || null,
      termsAccepted: true,
      privacyAccepted: true,
      ...(parsed.policyVersion ? { policyVersion: String(parsed.policyVersion) } : {}),
    };
  } catch {
    window.localStorage.removeItem(SIGNUP_CONSENT_STORAGE_KEY);
    return null;
  }
};

export const clearStoredSignupConsent = (email?: string | null) => {
  if (typeof window === 'undefined') {
    return;
  }

  if (typeof email === 'undefined') {
    window.localStorage.removeItem(SIGNUP_CONSENT_STORAGE_KEY);
    return;
  }

  const storedConsent = readStoredSignupConsent(email);
  if (storedConsent) {
    window.localStorage.removeItem(SIGNUP_CONSENT_STORAGE_KEY);
  }
};

export const getSignupConsentUpdatePayload = (
  snapshot: SignupConsentSnapshot
): UpdateConsentRequest => ({
  termsAccepted: snapshot.termsAccepted,
  privacyAccepted: snapshot.privacyAccepted,
  ...(snapshot.policyVersion ? { policyVersion: snapshot.policyVersion } : {}),
});
